import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import HeaderLoggedUser from "./partials/HeaderLoggedUser";
import {getCookie} from "./utils/cookies";
import AccountIntro from './partials/AccountIntro';
import ActionLink from './partials/ActionLink';
import { SITE_CONTACTS } from './partials/Header';
import { PRIVACY_PATH } from '../legal/legalDocuments';

const API_AUTH_URL = process.env.REACT_APP_AUTH_API_URL;

export default function Contacts() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        message: ''
    });

    const handleChange = (e) => {
        setSubmitStatus(null);
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const token = getCookie('MMAUTH');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_AUTH_URL}/auth/getLoggedFuneralHonorData`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'MmTkn': token
                    }
                });

                if (response.status === 200) {
                    const data = await response.json();

                    if (data !== null) {
                        setData(data);
                    } else {
                        setLoadError(true);
                    }
                } else if (response.status === 401 || response.status === 403) {
                    navigate("/login");
                    window.scrollTo(0, 0);
                } else {
                    setLoadError(true);
                }
            } catch (error) {
                setLoadError(true);
            }
        };

        fetchData();
    }, [navigate, token]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting || !data) return;
        if (!formData.message.trim()) {
            setSubmitStatus('validation');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch(`${API_AUTH_URL}/send-mail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: data?.title,
                    email: data?.email,
                    phone: data?.phone,
                    message: formData.message
                })
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({ message: '' });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="ef-page ef-account-page ef-support-page min-h-screen flex flex-col">
            <HeaderLoggedUser />
            <AccountIntro eyebrow="Contatti e assistenza" title="Siamo qui per aiutarti." backTo="/profilo" backLabel="Torna al profilo">Una modifica al profilo, una nuova sede o una domanda: il team Easy Funeral è a tua disposizione.</AccountIntro>
            <main className="ef-account-content ef-support-grid">
                <aside className="ef-support-details">
                    <section className="ef-account-panel">
                        <p className="ef-account-eyebrow">Parliamone insieme</p>
                        <h2>Un contatto diretto.</h2>
                        <p>Scrivici o chiamaci per ricevere assistenza dedicata alla tua agenzia.</p>
                        <div className="ef-support-channel"><span>Email</span><ActionLink action="email" targetType="platform" source="contacts" href={`mailto:${SITE_CONTACTS.email}`}>{SITE_CONTACTS.email} <span aria-hidden="true">↗</span></ActionLink></div>
                        <div className="ef-support-channel"><span>Chiama l’assistenza</span><ActionLink action="call" targetType="platform" source="contacts" href="tel:800123123">800 123 123 <span aria-hidden="true">↗</span></ActionLink></div>
                    </section>
                    <div className="ef-support-note"><h3>Al tuo fianco, anche fuori orario.</h3><p>Il nostro orario standard è 9:00–18:00. Garantiamo assistenza anche fuori orario, con risposta via email entro 8 ore.</p></div>
                </aside>
                    <form onSubmit={handleSubmit} className="ef-account-panel ef-support-form" aria-busy={isSubmitting}>
                        <h2>Come possiamo aiutarti?</h2>
                        <p>Descrivi la tua richiesta. Ti risponderemo all’email associata al tuo profilo.</p>
                        {loadError && <p role="alert" className="ef-account-notice ef-account-notice--error">Non è stato possibile caricare i tuoi recapiti. Ricarica la pagina o contattaci direttamente.</p>}
                        {!data && !loadError && <p role="status">Caricamento dei tuoi recapiti…</p>}
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="message" className="block mb-2 text-lg mb-4">
                                    Il tuo messaggio
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="5"
                                    required
                                    disabled={isSubmitting}
                                    className="w-full px-4 py-3 border-radius bg-white focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                    placeholder="Descrivi la tua richiesta..."
                                />
                            </div>

                            {submitStatus === 'validation' && (
                                <div role="alert" className="ef-account-notice ef-account-notice--error">
                                    Scrivi un messaggio per continuare.
                                </div>
                            )}

                            {submitStatus === 'success' && (
                                <div role="status" className="ef-account-notice">
                                    Messaggio inviato con successo! Ti risponderemo al più presto.
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div role="alert" className="ef-account-notice ef-account-notice--error">
                                    Errore nell'invio. Riprova o contattaci direttamente via email all'indirizzo info@easy-funeral.it.
                                </div>
                            )}

                            <p className="text-sm">
                                Usiamo i recapiti del tuo profilo e il messaggio per gestire la richiesta di assistenza,
                                come descritto nell’<a href={PRIVACY_PATH} target="_blank" rel="noopener noreferrer">informativa sul trattamento dei dati personali</a>.
                                Inserisci solo i dati necessari alla tua richiesta.
                            </p>

                            <button
                                type="submit"
                                disabled={isSubmitting || !data}
                                className={`w-full py-3 px-6 border-radius bg-primary text-white hover:text-black hover:bg-white/90 disabled:bg-white/50 disabled:cursor-not-allowed transition-colors ${
                                    isSubmitting || !data ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                {isSubmitting ? 'Invio in corso...' : 'Invia richiesta'}
                            </button>
                        </div>
                    </form>
            </main>
        </div>
    );
}
